/* eslint-disable react/jsx-props-no-spreading */
/**
 * @fileoverview Componente optimizado CustomerEventsMC con separación de lógica (Data Layer) y performance hooks.
 * @module CustomerEventsMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.1.0 - Optimización de memoización y early return
 */
import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../../components/ButtonMC';
import LoadingMC from '../../../components/LoadingMC';
import ActivityIconMC from './ActivityIconMC';
import { formatDate } from '../../../../util/utils';
import useCustomerEventsData from '../../../hooks/inbox/data/useCustomerEventsData';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';

const getEventAgentName = (event, t) => {
  if (event.agent_name === 'mia_customer_tag') {
    return t('chatbot_ai.general.activity.event.mia_customer_tag', { tag_name: event.tag_name });
  }
  if (['mia_buy_intention', 'mia_human_help', 'mia_generated_order', 'mia_payment_link', 'mia_system'].includes(event.agent_name)) {
    return t(`chatbot_ai.select_mia_flag_options.${event.agent_name.replace(/^mia_/, "")}`);
  }
  return event.agent_name;
};

const CustomerEventsMC = memo(({ customer, chatId }) => {
  const { t } = useTranslation();
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  // Data Layer: carga de eventos con paginación incremental
  const {
    handleAddActivity,
    events,
    pagination,
    loadMoreEvents,
    loading
  } = useCustomerEventsData(customer, 1, chatId);

  const toggleButtonConfig = useMemo(() => ({
    outlined: true,
    size: 'xs',
    variant: 'SECONDARY',
    className: 'tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-px-2 tw-py-1',
    iconClassName: '!tw-w-4 !tw-h-4 tw-flex-shrink-0 tw-text-blue-500'
  }), []);

  // Debug tracking con useTraceUpdate
  useTraceUpdate('CustomerEventsMC', {
    customerId: customer?.web_id,
    eventsCount: events.length,
    currentPage: pagination.currentPage,
    hasNextPage: pagination.hasNextPage,
    loading
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  // Memoiza el renderizado de la lista de eventos para evitar renders innecesarios
  const eventsList = useMemo(() => {
    if (events.length === 0) return null;
    return events.map((event, idx) => (
      <li
        key={`customer-event-${event.id}`}
        className={`tw-flex tw-gap-2 ${idx > 0 ? "tw-mt-[17px]" : ""} ${idx > 0 && events[idx - 1]?.extraSpace ? "tw-mt-[48px]" : ""}`}
      >
        <div
          className="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-full tw-ml-[-52px] tw-flex-shrink-0 tw-overflow-hidden tw-shadow-none tw-bg-gray-50"
        >
          <div className={`tw-flex tw-items-center tw-justify-center tw-rounded-full tw-overflow-hidden tw-aspect-square tw-flex-shrink-0 tw-w-10 tw-h-10 ${event.type_event === 'chatbot_activated' ? 'tw-text-[#50B77D]' : ''} ${event.type_event === 'chatbot_deactivated' ? 'tw-text-[#F8A808]' : ''} ${event.type_event === 'chatbot_insufficient_balance' ? 'tw-text-[#F8A808]' : ''}`}>
            <ActivityIconMC event={event} />
          </div>
        </div>
        <div className="tw-flex tw-flex-col tw-pt-1.5 tw-gap-x-2 tw-text-xs tw-text-gray-700">
          {(event.direction === 'internal' || Number(event.direction) === 0) && (
            <div>
              {['chatbot_activated', 'chatbot_deactivated', 'chatbot_insufficient_balance'].includes(event.type_event) ? (
                <span className="tw-font-normal">
                  {`${t(`chatbot_ai.events.${event.action}`)} ${t("chatbot_ai.events.by")}: `}
                </span>
              ) : (
                <span className="tw-font-normal">
                  {`${event.action}${event.agent_name ? ', por:' : ''} `}
                </span>
              )}
              <span className="tw-font-semibold">
                {`${getEventAgentName(event, t)} `}
              </span>
            </div>
          )}
          {(event.direction === 'external' || Number(event.direction) === 1) && (
            <div className="tw-flex tw-flex-col">
              <span className="tw-font-semibold">{event.type_event}</span>
              <div>
                <span className="tw-font-semibold">{`${t('InboxMC:event.category')}: `}</span>
                <span className="tw-font-normal">{event.category_name}</span>
              </div>
              <div>
                <span className="tw-font-semibold">{`${t('InboxMC:event.subcategory')}: `}</span>
                <span className="tw-font-normal">{event.subcategory_name}</span>
              </div>
              {event.source && (
                <div>
                  <span className="tw-font-semibold">{`${t('InboxMC:event.source')}: `}</span>
                  <span className="tw-font-normal">{event.source}</span>
                </div>
              )}
              {event.url_spec && (
                <div>
                  <span className="tw-font-semibold">{`${t('InboxMC:event.url_spec')}: `}</span>
                  <span className="tw-font-normal">{event.url_spec}</span>
                </div>
              )}
              <div>
                <span className="tw-font-semibold">{`${t('InboxMC:event.created_at')}: `}</span>
                <span className="tw-font-normal">{formatDate(retailer_info, event.created_at)}</span>
              </div>
            </div>
          )}
          <span className="tw-text-3xs tw-font-normal tw-text-gray-500 tw-mt-2">{formatDate(retailer_info, event.created_at, 'DD-MM-YYYY H:mm')}</span>
        </div>
      </li>
    ));
  }, [events, retailer_info, t]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-4">
      <LoadingMC loading={loading} />
      {!loading && (
        <div className="tw-flex tw-justify-center">
          <ButtonComponent
            outlined
            text={t('InboxMC:event.add_activity')}
            onClick={handleAddActivity}
            className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-mb-2"
          />
        </div>
      )}
      <div className="tw-w-full tw-pl-4">
        <ol
          id="activities"
          className="tw-mx-auto tw-flex tw-flex-col tw-pl-8 tw-border-l-2 tw-border-blue-500"
        >
          {eventsList}
        </ol>
      </div>
      {pagination.hasNextPage && (
        <div className="tw-flex tw-justify-center tw-mb-2">
          <ButtonComponent
            {...toggleButtonConfig}
            text={loading ? t('InboxMC:event.loading') : t('InboxMC:event.showMore')}
            icon="arrow-ios-downward-outline"
            onClick={loadMoreEvents}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
});

CustomerEventsMC.displayName = 'CustomerEventsMC';

export default CustomerEventsMC;
